import type { FC, ReactNode } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuth from '../hooks/useAuth';
import { peekPostLoginRedirect } from './postLoginRedirect';

interface GuestGuardProps {
  children?: ReactNode;
}

const DEFAULT_DESTINATION = '/app/reports/dashboard';

/** Only an INTERNAL path may be honored as a post-login destination — a value
 *  that is not a single-slash-rooted path (e.g. `//evil.com` or an absolute
 *  URL) is discarded, so a crafted `from` can never open-redirect off-site. */
const safeDestination = (from: unknown): string =>
  typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')
    ? from
    : DEFAULT_DESTINATION;

const GuestGuard: FC<GuestGuardProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation<{ from?: string } | undefined>();

  if (isAuthenticated) {
    // Return the user to wherever SignedInGuard sent them from (e.g. the MCP
    // consent page), else the app home. Prefer the in-memory history state,
    // falling back to the sessionStorage stash (survives reloads and the
    // Firebase auth re-init). PEEK only — the destination's own guard clears
    // the stash on arrival, so a competing redirect can't destroy the deep
    // link before it has actually been honored.
    const stored = peekPostLoginRedirect();
    return <Redirect to={safeDestination(location.state?.from ?? stored)} />;
  }

  return <>{children}</>;
};

GuestGuard.propTypes = {
  children: PropTypes.node,
};

export default GuestGuard;
